import { useState } from 'react';
import { createPortal } from 'react-dom';
import { hueFor, initials } from './avatar';
import { EmailDraftCard } from './EmailDraftCard';
import type { InboxCardData, EmailDraftCardData, ResultCardKind } from './types';

type InboxChip = InboxCardData['emails'][number];

type CardAction = (
  a: { kind: ResultCardKind; intent: 'open' | 'send' | 'compose'; data: any },
) => void | Promise<boolean | { subject?: string; body?: string } | null | void>;

/**
 * The priority inbox as a clickable grid of email "chips" — the emails the user
 * needs to deal with. Clicking a chip expands an inline reply composer
 * (EmailDraftCard in reply mode) with a "Xavior write" option that drafts a
 * contextual reply; send delivers in-thread.
 *
 * Mirrors ContactsGridCard's grid pattern. Reply compose/send route back through
 * `onCardAction` (kind: 'email_draft') so ChatBox owns the network calls.
 */
export function InboxResultCard({
  data,
  onCardAction,
}: {
  data: InboxCardData;
  onCardAction: CardAction;
}) {
  const emails = data.emails ?? [];
  const [selected, setSelected] = useState<InboxChip | null>(null);

  if (!emails.length) return null;

  if (selected) {
    const re = /^re:/i.test(selected.subject) ? selected.subject : `Re: ${selected.subject || '(no subject)'}`;
    const replyData: EmailDraftCardData = {
      to: selected.fromEmail,
      toName: selected.fromName || selected.fromEmail,
      subject: re,
      body: selected.aiDraft ?? '',
      threadId: selected.threadId,
      messageId: selected.messageId,
      accountId: selected.integrationId,
      replyMode: true,
    };
    // The reply composer pops OUT of the (often narrow) chat column into a
    // centered overlay — Gmail-compose style — so the To/Subject/Body fields
    // get real room instead of being squeezed to ~350px when a module is open
    // beside the chat. Portaled to <body> to escape the column's width clamp.
    const composer = (
      <EmailDraftCard
        data={replyData}
        onSend={async (edited) => {
          const r = await onCardAction({
            kind: 'email_draft',
            intent: 'send',
            data: { ...edited, threadId: selected.threadId, messageId: selected.messageId, accountId: selected.integrationId },
          });
          const ok = typeof r === 'boolean' ? r : undefined;
          if (ok) setSelected(null);
          return ok;
        }}
        onCompose={async (args) => {
          const r = await onCardAction({
            kind: 'email_draft',
            intent: 'compose',
            data: {
              ...args,
              threadId: selected.threadId,
              messageId: selected.messageId,
              accountId: selected.integrationId,
              replyMode: true,
              subject: re,
            },
          });
          return r && typeof r === 'object' ? r : undefined;
        }}
      />
    );
    return createPortal(
      <div
        className="rc-inbox-reply-overlay xv-scroll"
        role="dialog"
        aria-modal="true"
        onClick={() => setSelected(null)}
      >
        <div className="rc-inbox-reply-panel" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="rc-inbox-back" onClick={() => setSelected(null)}>
            ← Inbox
          </button>
          {composer}
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <div className="rc-card rc-inbox">
      <div className="rc-meta">
        <span className="rc-meta-kind">📥 INBOX</span>
        <span className="rc-meta-sep">·</span>
        <span className="rc-meta-match">{emails.length}</span>
      </div>

      <div className="rc-inbox-grid">
        {emails.map((m) => (
          <button
            key={m.messageId}
            type="button"
            className="rc-inbox-chip"
            onClick={() => setSelected(m)}
            title={`${m.fromName || m.fromEmail} — ${m.subject}`}
          >
            {m.senderPhotoUrl ? (
              <img className="rc-av" src={m.senderPhotoUrl} alt="" />
            ) : (
              <div
                className="rc-av rc-av-initials"
                style={{ background: hueFor(m.fromEmail || m.fromName || m.messageId) }}
              >
                {initials(m.fromName, m.fromEmail || '')}
              </div>
            )}
            <div className="rc-inbox-text">
              <span className="rc-inbox-sender">
                {m.isUnread && <span className="rc-inbox-dot" aria-label="unread" />}
                {m.fromName || m.fromEmail || 'Unknown'}
              </span>
              <span className="rc-inbox-subject">{m.subject || '(no subject)'}</span>
              <span className="rc-inbox-snippet">{m.snippet}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
